import { Users } from 'lucide-react'
import { cn } from '@/common/utils/cn'
import { useCountdown } from '../../hooks/useCountdown'
import { useServerNow } from '@shared/hooks/useServerTime'
import { INTERACTIVE_CARD } from '../../constants/auctionCard.constant'
import { AuctionCardData } from '../../types/auctionCard.type'
import { proposalMetric } from '../../utils/proposalMetric'
import AnimatedCountdown from '../AnimatedCountdown'
import CardLabel from '../CardLabel'
import { useNavigate } from 'react-router';

type Props = {
  data: AuctionCardData
}

const AuctionAvailableCard = ({ data }: Props) => {
  const { forwardingType, metricKind, companyName, equipmentModel, transportType, participants, endsAt, original, bestProposal, isSealed, startsAt } = data

  const navigate = useNavigate();

  const remaining = useCountdown(endsAt)
  const startsRemaining = useCountdown(startsAt)
  const isDateMetric = metricKind === 'date'
  const serverNow = useServerNow()
  const isOngoing = !startsAt || new Date(startsAt).getTime() <= serverNow

  return (
    <div
      className={cn('flex bg-white-100 border border-border-default rounded-lg overflow-hidden shrink-0', INTERACTIVE_CARD)}
      onClick={() => navigate(`/maintenance-establishment-choice/auction-entries/${data.id}`)}
    >
      <div className={cn('w-1 shrink-0', isOngoing ? 'bg-primary-blue' : 'bg-yellow-400')} />

      <div className="flex flex-1 min-w-0 flex-col gap-2 p-3">
        <CardLabel forwardingType={forwardingType} transportType={transportType} sealed={!!isSealed} />

        <div className="flex items-baseline gap-2">
          <p className="flex-1 min-w-0 truncate text-xs font-bold" title={equipmentModel}>
            {equipmentModel}
          </p>
          <p className="flex-1 min-w-0 truncate text-right text-[11px] text-white-400" title={companyName}>
            {companyName}
          </p>
        </div>

        <div
          key={isOngoing ? 'ongoing' : 'pending'}
          className="flex flex-col gap-2 animate-in fade-in slide-in-from-top-2 duration-500"
        >
          {isOngoing ? (
            <>
              <div className={cn('grid gap-x-3 gap-y-0.5 rounded-md border border-border-default bg-white-200 p-2 text-[11px]', isDateMetric ? 'grid-cols-1' : 'grid-cols-2')}>
                <span className="text-[9px] text-white-300">Melhor proposta</span>
                {!isDateMetric && <span className="text-[9px] text-white-300">Original</span>}
                {bestProposal ? (
                  <b className="text-success">{proposalMetric(isDateMetric, bestProposal)}</b>
                ) : (
                  <span className="italic text-white-300">Sem proposta</span>
                )}
                {!isDateMetric && original && <b className="text-white-400">{proposalMetric(isDateMetric, original)}</b>}
              </div>

              <div className="flex items-center justify-between gap-2 text-[10px] text-white-400">
                <span className="flex items-center gap-1">
                  <Users size={11} /> {participants} participantes
                </span>
                <span className="flex items-center gap-1">
                  Acaba em
                  <AnimatedCountdown value={remaining} className="font-bold text-white-800" />
                </span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between gap-2 border-t border-border-default pt-2">
              <span className="text-[9px] italic text-white-300">Começa em</span>
              <AnimatedCountdown value={startsRemaining} className="text-[11px] font-bold text-white-800" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuctionAvailableCard
